/**
 * Search API Service
 * Handles global search across tickets, activities, settings, and other data
 */

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.triagegenius.com'

class SearchAPI {
  /**
   * Perform global search across all data types
   */
  static async globalSearch(query, options = {}) {
    const {
      limit = 20,
      categories = ['tickets', 'activities', 'settings'],
      includeArchived = false
    } = options

    try {
      // httpOnly cookie auth - no token needed
      const params = new URLSearchParams({
        q: query,
        limit: limit.toString(),
        categories: categories.join(','),
        includeArchived: includeArchived.toString()
      })

      const response = await fetch(`${API_BASE}/api/search/global?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`)
      }

      const data = await response.json()
      return data.results || {
        tickets: [],
        activities: [],
        settings: [],
        suggestions: [],
        total: 0
      }
    } catch (error) {
      console.error('Global search error:', error)
      // Return mock data for development/fallback
      return this.getMockSearchResults(query)
    }
  }

  /**
   * Search specific data type
   */
  static async searchByType(type, query, options = {}) {
    const { limit = 10, filters = {} } = options

    try {
      // httpOnly cookie auth - no token needed
      const response = await fetch(`${API_BASE}/api/search/${type}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          query,
          limit,
          filters
        })
      })

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`)
      }

      const data = await response.json()
      return data.results || []
    } catch (error) {
      console.error(`${type} search error:`, error)
      return []
    }
  }

  /**
   * Get search suggestions based on query
   */
  static async getSearchSuggestions(query) {
    try {
      // httpOnly cookie auth - no token needed
      const response = await fetch(`${API_BASE}/api/search/suggestions?q=${encodeURIComponent(query)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error(`Suggestions failed: ${response.status}`)
      }

      const data = await response.json()
      return data.suggestions || []
    } catch (error) {
      console.error('Search suggestions error:', error)
      return this.getMockSuggestions(query)
    }
  }

  /**
   * Get mock search results for development/fallback
   */
  static getMockSearchResults(query) {
    const queryLower = query.toLowerCase()
    const results = {
      tickets: [],
      activities: [],
      settings: [],
      suggestions: [],
      total: 0
    }

    // Mock ticket results
    if (queryLower.includes('ticket') || queryLower.includes('billing') || queryLower.includes('support')) {
      results.tickets = [
        {
          id: 'ticket-001',
          title: 'Billing inquiry - Subscription upgrade',
          description: 'Customer requesting to upgrade from trial to professional plan',
          timestamp: new Date(Date.now() - 86400000).toISOString(),
          status: 'open',
          priority: 'high',
          category: 'billing'
        },
        {
          id: 'ticket-002', 
          title: 'Technical support - API integration',
          description: 'Help with webhook configuration and API authentication',
          timestamp: new Date(Date.now() - 172800000).toISOString(),
          status: 'pending',
          priority: 'normal',
          category: 'technical'
        }
      ]
    }

    // Mock activity results
    if (queryLower.includes('activity') || queryLower.includes('classified') || queryLower.includes('confidence')) {
      results.activities = [
        {
          id: 'activity-001',
          title: 'High confidence billing classification',
          description: 'Ticket automatically classified as billing inquiry with 94% confidence',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          type: 'classification',
          confidence: 0.94
        },
        {
          id: 'activity-002',
          title: 'Technical support classification',
          description: 'Ticket classified as technical support with sentiment analysis',
          timestamp: new Date(Date.now() - 7200000).toISOString(),
          type: 'classification',
          confidence: 0.89
        }
      ]
    }

    // Mock settings results
    if (queryLower.includes('setting') || queryLower.includes('config') || queryLower.includes('zendesk') || queryLower.includes('notification')) {
      results.settings = [
        {
          id: 'settings-zendesk',
          title: 'Zendesk Integration',
          description: 'Configure Zendesk API connection and webhook settings',
          section: 'zendesk',
          url: '/app/settings'
        },
        {
          id: 'settings-notifications',
          title: 'Notification Settings', 
          description: 'Email alerts and webhook notifications',
          section: 'notifications',
          url: '/app/settings'
        },
        {
          id: 'settings-classification',
          title: 'Classification Configuration',
          description: 'AI model settings, confidence thresholds, and category mappings',
          section: 'classification', 
          url: '/app/settings'
        }
      ]
    }

    // General suggestions
    if (query.length > 0) {
      results.suggestions = [
        'billing inquiry',
        'technical support',
        'classification confidence',
        'zendesk webhook',
        'notification settings',
        'recent activity'
      ].filter(s => s.toLowerCase().includes(queryLower))
    }

    results.total = results.tickets.length + results.activities.length + results.settings.length

    return results
  }

  /**
   * Get mock suggestions for development/fallback
   */
  static getMockSuggestions(query) {
    const allSuggestions = [
      'billing inquiry',
      'technical support',
      'feature request',
      'urgent escalation',
      'classification confidence',
      'zendesk integration',
      'webhook settings',
      'notification alerts',
      'recent activity',
      'category performance',
      'api configuration',
      'sentiment analysis'
    ]

    return allSuggestions
      .filter(s => s.toLowerCase().includes(query.toLowerCase()))
      .slice(0, 5)
  }
}

export default SearchAPI